import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function LimitedEditionPage() {
  const { t } = useTranslation();
  const [artworks, setArtworks] = useState([]);
  const [activeCollection, setActiveCollection] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArtworks();
  }, [activeCollection]);

  const fetchArtworks = async () => {
    try {
      setLoading(true);
      const params = activeCollection !== "all" ? { collection: activeCollection } : {};
      const res = await axios.get(`${API}/artworks`, { params });
      setArtworks(res.data);
    } catch (err) {
      console.error("Error fetching artworks:", err);
    } finally {
      setLoading(false);
    }
  };

  const coreWorks = artworks.filter((a) => a.collection === "core");
  const signatureWorks = artworks.filter((a) => a.collection === "signature");

  return (
    <div data-testid="limited-edition-page">
      <SEO titleKey="seo.limited_edition_title" descriptionKey="seo.limited_edition_description" path="/limited-edition" />
      {/* Header */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <p
              className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-4"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {t("limited_edition.page_eyebrow")}
            </p>
          </motion.div>
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A1B1A] max-w-4xl mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
          >
            {t("limited_edition.header_1")} <span style={{ fontWeight: 600 }}>{t("limited_edition.header_2")}</span>
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
            className="text-sm sm:text-base text-[#4A4A4A] leading-relaxed max-w-2xl"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
          >
            {t("limited_edition.header_sub")}
          </motion.p>
        </div>
      </section>

      {/* Collection Filter */}
      <section className="pb-8" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={3}
            className="flex flex-wrap gap-3"
            data-testid="collection-filters"
          >
            {[
              { key: "all", label: t("limited_edition.filter_all") },
              { key: "core", label: t("limited_edition.filter_core") },
              { key: "signature", label: t("limited_edition.filter_signature") },
            ].map(({ key, label }) => (
              <button
                key={key}
                data-testid={`filter-${key}`}
                onClick={() => setActiveCollection(key)}
                className={`text-xs uppercase tracking-[0.15em] px-6 py-3 border transition-all duration-300 ${
                  activeCollection === key
                    ? "bg-[#1A1B1A] text-[#FAF9F6] border-[#1A1B1A]"
                    : "bg-transparent text-[#4A4A4A] border-[#D8D4CC] hover:border-[#1A1B1A]"
                }`}
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}
              >
                {label}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-20" style={{ backgroundColor: "#FAF9F6" }}>
          <div className="w-8 h-8 border border-[#C4A462] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Core Collection */}
          {(activeCollection === "all" || activeCollection === "core") && coreWorks.length > 0 && (
            <section data-testid="core-collection" className="py-16 lg:py-24" style={{ backgroundColor: "#FAF9F6" }}>
              <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  className="mb-12"
                >
                  <div className="luxury-divider mb-6" />
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <h2
                        className="text-3xl sm:text-4xl tracking-tighter text-[#1A1B1A]"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                      >
                        {t("limited_edition.filter_core")}
                      </h2>
                      <p
                        className="text-sm text-[#757575] mt-2"
                        style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                      >
                        {t("limited_edition.core_desc")}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                  {coreWorks.map((artwork, i) => (
                    <ArtworkCard key={artwork.id} artwork={artwork} index={i} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Signature Collection */}
          {(activeCollection === "all" || activeCollection === "signature") && signatureWorks.length > 0 && (
            <section data-testid="signature-collection" className="py-16 lg:py-24 grain-overlay" style={{ backgroundColor: "#F3F1EC" }}>
              <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  className="mb-12"
                >
                  <div className="luxury-divider mb-6" />
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <h2
                        className="text-3xl sm:text-4xl tracking-tighter text-[#1A1B1A]"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                      >
                        {t("limited_edition.filter_signature")}
                      </h2>
                      <p
                        className="text-sm text-[#757575] mt-2"
                        style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                      >
                        {t("limited_edition.signature_desc")}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                  {signatureWorks.map((artwork, i) => (
                    <ArtworkCard key={artwork.id} artwork={artwork} index={i} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* Custom Commission CTA */}
      <section data-testid="custom-commission-cta" className="py-24 lg:py-32 grain-overlay" style={{ backgroundColor: "#1A1B1A" }}>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p
              className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-6"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {t("limited_edition.commission_eyebrow")}
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#FAF9F6] mb-6 max-w-3xl mx-auto"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
            >
              {t("limited_edition.commission_title_1")} <span style={{ fontWeight: 600 }}>{t("limited_edition.commission_title_2")}</span>
            </h2>
            <p
              className="text-sm text-[#757575] leading-relaxed mb-10 max-w-lg mx-auto"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
            >
              {t("limited_edition.commission_desc")}
            </p>
            <Link to="/contact" data-testid="commission-cta-btn">
              <button
                className="bg-[#C4A462] text-[#FAF9F6] px-10 py-4 text-xs uppercase tracking-[0.15em] hover:bg-[#A98A4E] transition-colors duration-300 flex items-center gap-3 mx-auto"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
              >
                {t("limited_edition.commission_cta")} <ArrowRight strokeWidth={1.25} size={16} />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function ArtworkCard({ artwork, index }) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-30px" }}
      variants={fadeUp}
      custom={index}
    >
      <Link
        to={`/limited-edition/${artwork.id}`}
        data-testid={`artwork-card-${artwork.id}`}
        className="group block"
      >
        <div className="img-hover-zoom mb-5">
          <img
            src={artwork.image}
            alt={artwork.title}
            className="w-full h-72 lg:h-96 object-cover"
          />
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3
              className="text-xl lg:text-2xl tracking-tighter text-[#1A1B1A] group-hover:text-[#C4A462] transition-colors duration-300"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            >
              {artwork.title}
            </h3>
            <p
              className="text-xs text-[#757575] mt-1"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}
            >
              {artwork.dimensions} &bull; {artwork.medium}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p
              className="text-xs uppercase tracking-[0.15em] text-[#C4A462]"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {artwork.collection === "signature" ? t("limited_edition.filter_signature") : t("limited_edition.filter_core")}
            </p>
            <p
              className="text-xs text-[#757575] mt-1"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {artwork.edition_available}/{artwork.edition_total} {t("limited_edition.card_available")}
            </p>
          </div>
        </div>
        <p
          className="text-[11px] text-[#757575] mt-3 italic"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
        >
          {t("limited_edition.card_certificate")}
        </p>
      </Link>
    </motion.div>
  );
}
